import AdSlot from "../../../components/base/AdSlot";
import type { Quiz } from "../../../mocks/quizzes";

interface Props {
  quiz: Quiz;
  onStart: () => void;
}

function formatPlays(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}K`;
  return String(n);
}

export default function QuizStart({ quiz, onStart }: Props) {
  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden">
          {/* Image Side */}
          <div className="relative min-h-[320px] md:min-h-full">
            <img
              src={quiz.image}
              alt={quiz.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            {/* Stats overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 flex gap-6">
              <div className="text-center">
                <p className="text-white font-extrabold text-xl"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {formatPlays(quiz.totalPlays)}
                </p>
                <p className="text-white/70 text-xs">Players</p>
              </div>
              <div className="text-center">
                <p className="text-white font-extrabold text-xl"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {quiz.rating}★
                </p>
                <p className="text-white/70 text-xs">Rating</p>
              </div>
              <div className="text-center">
                <p className="text-white font-extrabold text-xl"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {quiz.questions.length}
                </p>
                <p className="text-white/70 text-xs">Questions</p>
              </div>
            </div>
          </div>

          {/* Info Side */}
          <div className="p-8 md:p-10 flex flex-col justify-center">
            {/* Category Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs font-bold text-[#FF6B6B] uppercase tracking-wider bg-orange-50 px-3 py-1 rounded-full">
                {quiz.categoryLabel}
              </span>
              <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full font-medium">
                {quiz.tag}
              </span>
            </div>

            <h1
              className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4 leading-tight"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              {quiz.title}
            </h1>

            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              {quiz.description}
            </p>

            {/* Quick Facts */}
            <div className="flex gap-4 mb-8">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-time-line text-[#FF6B6B]" />
                <span>{quiz.estimatedTime}</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-question-line text-[#FF6B6B]" />
                <span>{quiz.questions.length} questions</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <i className="ri-gift-line text-[#FF6B6B]" />
                <span>{quiz.results.length} possible results</span>
              </div>
            </div>

            <button
              onClick={onStart}
              className="w-full py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white font-extrabold text-base rounded-2xl hover:scale-105 transition-transform cursor-pointer whitespace-nowrap shadow-lg"
            >
              Start Quiz Now! ✨
            </button>

            <p className="text-center text-xs text-gray-400 mt-3">
              Free · No login required · Shareable results
            </p>
          </div>
        </div>

        {/* ── Ad: Rectangle below quiz card ── */}
        <div className="mt-8 flex justify-center">
          <div className="hidden md:block">
            <AdSlot size="leaderboard" />
          </div>
          <div className="md:hidden w-full flex justify-center">
            <AdSlot size="large-rectangle" />
          </div>
        </div>
      </div>
    </div>
  );
}
